import { PaymentMethods } from '@arema/components/Classes';
import { addCommas } from '@arema/components/Util';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Header, Icon, Table, Modal} from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import API from '../API';

interface EventDepositSourcesModalProps{
	open: boolean,
	eventId: number,
	onClose: ()=>void,
}

interface DepositSourceData{
	event_id: number,
	payment_method: PaymentMethods,
	method_name_internal: string,
	payment_source: number,
	source_name: string,
	business_id: number,
	ticket_cost: number,
	commission: number,
}

var EventDepositSourcesModal = (props: EventDepositSourcesModalProps)=>{
	var { t } = useTranslation();
	var [loadError, setLoadError] = useState<string>(null);
	var [data, setData] = useState<{
		event_id: number,
		data: DepositSourceData[]
	}>(null);

	useEffect(()=>{
		if(!props.open || !props.eventId) return;
		if(data?.event_id===props.eventId) return;

		setLoadError(null);
		API.getEventDepositSources(props.eventId).then(res=>{
			if(res.error) return setLoadError(res.message);
			setData({
				event_id: props.eventId,
				data: res.data
			});
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-EVDPSRC-1' }));
		});
	}, [props.open, data, props.eventId]);

	var {
		business_total,
		data_total
	} = useMemo(()=>{
		var business_total : {
			business_id: number,
			commission: number,
			ticket_cost: number,
		}[] = [];
		var data_total = {
			ticket_cost: 0,
			commission: 0,
		}

		if(!data) return { business_total, data_total  };
		for(var i of data.data){
			data_total.ticket_cost += i.ticket_cost;
			data_total.commission += i.commission;

			var bx = business_total.findIndex(a=>a.business_id===(i.business_id || 1));
			if(bx==-1) bx = business_total.push({
				business_id: (i.business_id || 1),
				commission: 0,
				ticket_cost: 0
			})-1;

			business_total[bx].ticket_cost += i.ticket_cost;
			business_total[bx].commission += i.commission;
		}
		return { business_total, data_total };
	}, [data]);
	
	return <Modal size='large' open={props.open} onClose={props.onClose}>
		<Modal.Header>{t('sources_modal.title')}</Modal.Header>
		<Modal.Content>
			{(!data || loadError) ? (
				<Header loading={!loadError} text={loadError ? t('common.error') : t('common.loading')} subtext={loadError} />
			) : <>
				<Table fitted top>
					<Table.Head
						collapsingIndexes={[0, 1, 3, 4, 5, 6, 7]}
						data={[['ID', 'Método', 'Origen', 'Negocio', 'Cargo servicio', 'Boletos', 'Total']]}
					/>
					<Table.Body>
						{data.data.map(a=>(
							<Table.Row 
								key={`${a.payment_method}${a.payment_source ? '-' : ''}${a.payment_source || ''}`} 
								collapsingIndexes={[0, 1, 3, 4, 5, 6]} 
								data={[
									`${a.payment_method}${a.payment_source ? '-' : ''}${a.payment_source || ''}`,
									a.method_name_internal,
									a.source_name || <Icon name='minus' color='gray' />,
									a.business_id ? t(`source_business.business_${a.business_id}`) : <Icon name='minus' color='gray' />,
									addCommas(a.commission),
									addCommas(a.ticket_cost),
									addCommas(a.ticket_cost+a.commission),
								]} 
							/>
						))}
						<Table.Row collapsingIndexes={[0, 1, 3, 4, 5, 6]} header>
							<Table.Cell colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</Table.Cell>
							<Table.Cell>{addCommas(data_total.commission)}</Table.Cell>
							<Table.Cell>{addCommas(data_total.ticket_cost)}</Table.Cell>
							<Table.Cell>{addCommas(data_total.ticket_cost+data_total.commission)}</Table.Cell>
						</Table.Row>
					</Table.Body>
				</Table>
				{!!business_total && (
					<Table fitted last>
						<Table.Head
							collapsingIndexes={[0, 2, 3, 4]}
							data={[['ID', 'Negocio', 'Cargo servicio', 'Boletos', 'Total']]}
						/>
						<Table.Body
							data={business_total.map(a=>([
								a.business_id,
								t(`source_business.business_${a.business_id}`),
								addCommas(a.commission),
								addCommas(a.ticket_cost),
								addCommas(a.ticket_cost+a.commission),
							]))}
						/>
					</Table>
				)}
			</>}
		</Modal.Content>
		<Modal.Actions>
			<Button text={t('common.close')} onClick={props.onClose} />
		</Modal.Actions>
	</Modal>
}

export default EventDepositSourcesModal;